import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Eye, EyeOff, Lock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <Lock className="w-16 h-16 text-gray-300 mx-auto mb-6" />
          <h1 className="font-heading text-2xl font-bold text-gray-800 mb-4">Invalid Reset Link</h1>
          <p className="text-gray-500 font-body mb-8">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password"
            className="inline-block font-heading font-bold py-3 px-8 rounded-full text-white bg-edupaws-navy shadow-[0_4px_0_#3C4753] hover:shadow-[0_2px_0_#3C4753] hover:translate-y-[2px] transition-all">
            Request New Link
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-edupaws-sage/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-edupaws-sage" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-gray-800 mb-4">Password Updated</h1>
          <p className="text-gray-500 font-body mb-8">Your password has been reset. You can now sign in with your new password.</p>
          <Link to="/login"
            data-testid="go-to-login"
            className="inline-block font-heading font-bold py-4 px-10 rounded-full text-lg text-white bg-edupaws-navy shadow-[0_4px_0_#3C4753] hover:shadow-[0_2px_0_#3C4753] hover:translate-y-[2px] transition-all">
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <button onClick={() => navigate('/login')}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-8 font-body">
          <ArrowLeft className="w-4 h-4" /> Back to login
        </button>

        <div className="flex items-center gap-3 mb-8">
          <img src="https://edupaws.co/cdn/shop/files/Edu_Paws_Logo_Updated.png?v=1759285352&width=100" alt="Edu Paws" className="h-10" />
        </div>

        <div className="w-16 h-16 bg-edupaws-sage/20 rounded-2xl flex items-center justify-center mb-6">
          <Lock className="w-8 h-8 text-edupaws-sage" />
        </div>

        <h1 className="font-heading text-3xl font-bold text-gray-800 mb-2">Set New Password</h1>
        <p className="text-gray-500 font-body mb-8">Choose a strong password for your account.</p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label className="font-body font-medium text-gray-700">New Password</Label>
            <div className="relative mt-1.5">
              <Input
                data-testid="new-password-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                required minLength={6}
                className="h-12 rounded-xl border-2 border-gray-200 pr-12 font-body focus:border-edupaws-sage"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="font-body font-medium text-gray-700">Confirm Password</Label>
            <Input
              data-testid="confirm-password-input"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              required
              className="mt-1.5 h-12 rounded-xl border-2 border-gray-200 font-body focus:border-edupaws-sage"
            />
          </div>
          <button type="submit" disabled={loading}
            data-testid="reset-password-submit"
            className="w-full font-heading font-bold py-4 rounded-full text-lg text-white transition-all
              bg-edupaws-navy shadow-[0_4px_0_#3C4753] hover:shadow-[0_2px_0_#3C4753] hover:translate-y-[2px]
              disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
